import { useCallback, useMemo, useState } from "react"

import { Typography } from "@mui/material"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { useNetworksDispatchContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import * as Styled from "./MobileSubNetworkSelector.styled"

export function MobileSubNetworkSelector(props) {

    const {
        selectedNetwork,
        selectedSubNetworkSlugs,
        onClose,
    } = props

    const networksDispatch = useNetworksDispatchContext()
    const { setImageFeatureClickedPosition } = useImageCollectionDispatchContext()
    const { swapToNetwork } = networksDispatch
    const { t } = useTranslation(I18N_NAMESPACE)

    const [checkedSubNetworks, setCheckedSubNetworks] = useState(selectedSubNetworkSlugs || [])

    const changeSelectedSubNetworksSlug = useCallback(() => {
        swapToNetwork(selectedNetwork.slug, checkedSubNetworks)
        setImageFeatureClickedPosition()

        onClose("apply")
    }, [selectedNetwork, checkedSubNetworks, setImageFeatureClickedPosition, swapToNetwork, onClose])

    const subNetworksList = useMemo(() => {
        const checkboxes = []
        const isIndeterminate = checkedSubNetworks.length !== 0 && checkedSubNetworks.length !== selectedNetwork.children.length
        checkboxes.push(
            <Styled.Checkbox
                key={selectedNetwork?.slug}
                color="primary"
                indeterminate={isIndeterminate}
                label={t(I18N_NAMESPACE, "containers.networkSelector.allSubNetworks")}
                labelPosition="start"
                value={selectedNetwork?.children?.every((subnetwork) => checkedSubNetworks.includes(subnetwork.slug))}
                onChange={(_, checked) => {
                    if (checked && !isIndeterminate) {
                        setCheckedSubNetworks(selectedNetwork?.children?.map((subnetwork => subnetwork.slug)) || [])
                    } else {
                        setCheckedSubNetworks([])
                    }
                }}
                size="small"
                slotProps={{
                    typography: { variant: "Desktop & Mobile/Heading/H3" },
                }}
            />,
        )
        selectedNetwork?.children?.map(subnetwork => (
            checkboxes.push(<Styled.Checkbox
                key={subnetwork.slug}
                color="primary"
                labelPosition="start"
                label={subnetwork.name}
                value={checkedSubNetworks.includes(subnetwork.slug)}
                onChange={(_, checked) => {
                    if (checked) {
                        setCheckedSubNetworks((prev) => [...prev, subnetwork.slug])
                    } else {
                        setCheckedSubNetworks((prev) => prev.filter((slug) => slug !== subnetwork.slug))
                    }
                }}
                size="small"
            />)
        ))

        return checkboxes
    }, [setCheckedSubNetworks, checkedSubNetworks, selectedNetwork, t])

    const canApply = useMemo(() => {
        return checkedSubNetworks.length > 0
    }, [checkedSubNetworks])

    if (!selectedNetwork) {
        return null
    }

    return (
        <Styled.Container>
            <Typography variant="Desktop & Mobile/Heading/H1">{selectedNetwork.name}</Typography>
            <Styled.SubNetworksContainer>
                {subNetworksList}
            </Styled.SubNetworksContainer>
            <Styled.ButtonsContainer>
                <Styled.Button
                    variant="tertiary"
                    onClick={() => onClose("cancel")}
                >
                    {t(I18N_NAMESPACE, "containers.networkSelector.cancel")}
                </Styled.Button>
                <Styled.Button
                    disabled={!canApply}
                    onClick={changeSelectedSubNetworksSlug}
                >
                    {t(I18N_NAMESPACE, "containers.networkSelector.apply")}
                </Styled.Button>
            </Styled.ButtonsContainer>
        </Styled.Container>
    )
}

MobileSubNetworkSelector.propTypes = {
    selectedNetwork: PropTypes.object.isRequired,
    selectedSubNetworkSlugs: PropTypes.arrayOf(PropTypes.string),
    onClose: PropTypes.func.isRequired,
}
